import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../hooks/useAuth';

interface TourStep {
  id: string;
  title: string;
  content: string;
  target?: string;
  path: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  requiresRole?: string[];  // Optional: restrict step to specific roles
}

interface TourContextType {
  isActive: boolean;
  currentStep: number;
  tourSteps: TourStep[];
  hasHiddenTourPermanently: boolean;
  startTour: () => void;
  startPageTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  hideTourPermanently: () => void;
  resetTourSettings: () => void;
}

// Full site tour
const fullTourSteps: TourStep[] = [
  {
    id: 'dashboard',
    title: 'Welcome to FlowDocs!',
    content: 'This is your dashboard. Here you can see all your connected Salesforce organizations and quickly access key features. Click on any org card to view its flows.',
    path: '/dashboard',
    target: '.org-grid',
    position: 'bottom'
  },
  {
    id: 'orgs',
    title: 'Organization Management',
    content: 'Manage all your connected Salesforce organizations. Add new orgs, refresh authentication, configure sync settings, and control user access.',
    path: '/orgs',
    target: 'h1',
    position: 'bottom'
  },
  {
    id: 'flows',
    title: 'Flows Page',
    content: 'View all flows in your organization with performance grades. Click on grade tiles to filter flows, use the search bar to find specific flows, and click any flow name to see detailed analysis.',
    path: '/flows',
    target: '.flows-table',
    position: 'top'
  },
  {
    id: 'triggers',
    title: 'Entry Criteria (Triggers)',
    content: 'See all record-triggered flows and their entry conditions. Filter by object, trigger type, and specific fields to understand what causes flows to execute.',
    path: '/triggers',
    target: '.triggers-table',
    position: 'top'
  },
  {
    id: 'fields',
    title: 'Field Impact Analysis',
    content: 'Discover which flows read or modify specific fields. Essential for change impact analysis - know exactly what will be affected before making changes to your data model.',
    path: '/fields',
    target: '.fields-table',
    position: 'top'
  },
  {
    id: 'help',
    title: 'Help & Support',
    content: 'Access our comprehensive knowledge base for best practices, troubleshooting guides, and detailed documentation. Contact support directly when you need assistance.',
    path: '/help',
    target: 'h1',
    position: 'bottom'
  }
];

// Page-specific tours
const pageTours: Record<string, TourStep[]> = {
  '/flows': [
    {
      id: 'flows-overview',
      title: 'Flow Management Center',
      content: 'This is your central hub for managing all Salesforce flows. You can see performance grades, search for specific flows, and access detailed documentation.',
      path: '/flows',
      target: 'h1',
      position: 'bottom'
    },
    {
      id: 'flows-grades',
      title: 'Performance Grades',
      content: 'Each flow receives a grade (A-F) based on complexity, best practices, and potential issues. Click on any grade tile to filter flows by that grade.',
      path: '/flows',
      target: '.grade-tiles',
      position: 'bottom'
    },
    {
      id: 'flows-search',
      title: 'Search and Filter',
      content: 'Use the search bar to find flows by name, type, or description. You can also filter by status (Active/Inactive) and other criteria.',
      path: '/flows',
      target: 'input[type="search"]',
      position: 'bottom'
    },
    {
      id: 'flows-table',
      title: 'Flow Details Table',
      content: 'View all your flows with key information like type, status, and last modified date. Click any flow name to see detailed analysis and documentation.',
      path: '/flows',
      target: 'table',
      position: 'top'
    },
    {
      id: 'flows-actions',
      title: 'Quick Actions',
      content: 'Use the action buttons to sync flows from Salesforce, export data, or access flow-specific features. The Update button checks for changes in Salesforce.',
      path: '/flows',
      target: '.action-buttons',
      position: 'left',
      requiresRole: ['Admin', 'Super-Admin']  // Only show for admins
    }
  ],
  '/triggers': [
    {
      id: 'triggers-overview',
      title: 'Entry Criteria Overview',
      content: 'View all record-triggered flows and understand what causes them to execute. Essential for debugging and impact analysis.',
      path: '/triggers',
      target: 'h1',
      position: 'bottom'
    },
    {
      id: 'triggers-filters',
      title: 'Filter by Object',
      content: 'Filter flows by the Salesforce object they operate on. Quickly find all flows that affect Accounts, Contacts, or any other object.',
      path: '/triggers',
      target: '.object-filter',
      position: 'bottom'
    },
    {
      id: 'triggers-conditions',
      title: 'Entry Conditions',
      content: 'See the exact conditions that trigger each flow. Understand when flows run: on create, update, delete, or undelete.',
      path: '/triggers',
      target: '.trigger-conditions',
      position: 'right'
    }
  ],
  '/dashboard': [
    {
      id: 'dashboard-welcome',
      title: 'Welcome to Your Dashboard',
      content: 'This is your FlowDocs command center. See all connected Salesforce orgs and their sync status at a glance.',
      path: '/dashboard',
      target: 'h1',
      position: 'bottom'
    },
    {
      id: 'dashboard-orgs',
      title: 'Organization Cards',
      content: 'Each card represents a connected Salesforce org. Click on a card to view flows, or use the quick actions to sync or manage settings.',
      path: '/dashboard',
      target: '.org-grid',
      position: 'top'
    },
    {
      id: 'dashboard-stats',
      title: 'Flow Statistics',
      content: 'See the total number of flows, active flows, and flows needing review. These metrics help you track documentation coverage.',
      path: '/dashboard',
      target: '.stats-section',
      position: 'bottom'
    }
  ],
  '/fields': [
    {
      id: 'fields-overview',
      title: 'Field Impact Analysis',
      content: 'This page helps you understand which flows interact with specific fields in your Salesforce org. Essential for impact analysis before making field changes.',
      path: '/fields',
      target: 'h1',
      position: 'bottom'
    },
    {
      id: 'fields-search',
      title: 'Search Objects and Fields',
      content: 'Use the search bar to find specific objects or fields. You can search by object name (e.g., "Account") or field name (e.g., "Status").',
      path: '/fields',
      target: 'input[type="search"]',
      position: 'bottom'
    },
    {
      id: 'fields-filter',
      title: 'Filter by Operation',
      content: 'Filter fields by how flows interact with them: Read (flows that check field values), Write (flows that update fields), or Both.',
      path: '/fields',
      target: '.operation-filter',
      position: 'bottom'
    },
    {
      id: 'fields-table',
      title: 'Field Impact Table',
      content: 'View all fields and their flow interactions. Click on any field to see which specific flows read or write to it.',
      path: '/fields',
      target: 'table',
      position: 'top'
    },
    {
      id: 'fields-flow-count',
      title: 'Flow Count Indicators',
      content: 'The numbers show how many flows interact with each field. Green indicates read operations, blue indicates write operations.',
      path: '/fields',
      target: '.flow-count',
      position: 'left'
    },
    {
      id: 'fields-impact',
      title: 'Impact Analysis',
      content: 'Before modifying or deleting a field, check this page to understand which flows will be affected. This prevents breaking flows in production.',
      path: '/fields',
      target: '.impact-section',
      position: 'bottom'
    }
  ]
};

const TourContext = createContext<TourContextType | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourSteps, setTourSteps] = useState<TourStep[]>(fullTourSteps);
  const [hasSeenTour, setHasSeenTour] = useState(false);
  const [hasHiddenTourPermanently, setHasHiddenTourPermanently] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [tourOrgId, setTourOrgId] = useState<string | null>(null);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user has seen the tour or hidden it permanently
  useEffect(() => {
    const tourSeen = localStorage.getItem('flowdocs_tour_completed');
    const tourHidden = localStorage.getItem('flowdocs_tour_hidden_permanently');
    setHasSeenTour(!!tourSeen);
    setHasHiddenTourPermanently(!!tourHidden);
  }, []);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setUserRole(null);
        return;
      }

      const currentAccountId = localStorage.getItem('currentAccountId');
      if (!currentAccountId) {
        setUserRole(null);
        return;
      }

      try {
        const { data: userAccountRole } = await supabase
          .from('user_account_roles')
          .select(`
            *,
            roles (
              name
            )
          `)
          .eq('user_id', user.id)
          .eq('account_id', currentAccountId)
          .single();

        if (userAccountRole?.roles?.name) {
          setUserRole(userAccountRole.roles.name);
        }
      } catch (error) {
        console.error('Error fetching user role for tour:', error);
        setUserRole(null);
      }
    };

    fetchUserRole();
  }, [user, location]);

  // Auto-show tour for new users (unless they've hidden it permanently)
  useEffect(() => {
    if (!hasSeenTour && !hasHiddenTourPermanently && location.pathname === '/dashboard' && !isActive) {
      const timer = setTimeout(() => {
        setIsActive(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasSeenTour, hasHiddenTourPermanently, location.pathname, isActive]);

  const startTour = () => {
    // Extract and store orgId from current path if present
    const pathParts = location.pathname.split('/');
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const orgId = pathParts[1] && uuidRegex.test(pathParts[1]) ? pathParts[1] : null;
    setTourOrgId(orgId);

    setTourSteps(fullTourSteps);
    setIsActive(true);
    setCurrentStep(0);

    // Navigate to first step with orgId if we have one
    const firstStepPath = fullTourSteps[0].path;
    const targetPath = orgId && (firstStepPath === '/dashboard' || firstStepPath === '/flows' || firstStepPath === '/triggers' || firstStepPath === '/fields')
      ? `/${orgId}${firstStepPath}`
      : firstStepPath;

    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  // Filter tour steps based on user role
  const filterStepsByRole = (steps: TourStep[]): TourStep[] => {
    if (!steps) return [];

    return steps.filter(step => {
      // If no role requirement, show to everyone
      if (!step.requiresRole || step.requiresRole.length === 0) {
        return true;
      }

      // If user role is not set, hide restricted steps
      if (!userRole) {
        return false;
      }

      // Check if user has one of the required roles
      return step.requiresRole.includes(userRole);
    });
  };

  const startPageTour = () => {
    console.log('Starting page tour...');
    // Find the appropriate tour for the current page
    const currentPath = location.pathname;
    console.log('Current path:', currentPath);
    let pagePath = currentPath;

    // Extract the base path (remove orgId if present)
    const pathParts = currentPath.split('/');
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (pathParts[1] && uuidRegex.test(pathParts[1])) {
      // Path has orgId, extract the page type
      pagePath = '/' + (pathParts[2] || 'dashboard');
      console.log('Extracted page path (removed orgId):', pagePath);
    }

    console.log('Available tours:', Object.keys(pageTours));
    const pageTour = pageTours[pagePath];
    console.log('Found page tour:', pageTour);

    if (pageTour && pageTour.length > 0) {
      // Filter steps based on user role
      const filteredSteps = filterStepsByRole(pageTour);
      console.log('Filtered tour steps based on role:', userRole, 'from', pageTour.length, 'to', filteredSteps.length, 'steps');

      if (filteredSteps.length > 0) {
        setTourSteps(filteredSteps);
        setIsActive(true);
        setCurrentStep(0);
      } else {
        console.log('No tour steps available for current role');
        // If no steps available for this role, don't start the tour
      }
    } else {
      console.log('No page-specific tour found, starting full tour');
      // No tour available for this page, start the full tour
      startTour();
    }
  };

  const endTour = () => {
    setIsActive(false);
    localStorage.setItem('flowdocs_tour_completed', 'true');
    setHasSeenTour(true);
  };

  const navigateToStep = (stepIndex: number) => {
    const step = tourSteps[stepIndex];
    let targetPath = step.path;

    // Use the orgId captured at tour start for pages that need it
    if (tourOrgId && (targetPath === '/flows' || targetPath === '/triggers' || targetPath === '/fields' || targetPath === '/dashboard')) {
      targetPath = `/${tourOrgId}${targetPath}`;
    }

    setCurrentStep(stepIndex);
    navigate(targetPath);
  };

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStepIndex = currentStep + 1;
      const nextStepData = tourSteps[nextStepIndex];
      const currentStepData = tourSteps[currentStep];

      // Check if we need to navigate to a different page
      if (nextStepData.path !== currentStepData.path) {
        // Full site tour - navigate to the next page
        navigateToStep(nextStepIndex);
      } else {
        // Page-specific tour - just increment step
        setCurrentStep(nextStepIndex);
      }
    } else {
      endTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const prevStepIndex = currentStep - 1;
      const prevStepData = tourSteps[prevStepIndex];
      const currentStepData = tourSteps[currentStep];

      // Check if we need to navigate to a different page
      if (prevStepData.path !== currentStepData.path) {
        // Full site tour - navigate to the previous page
        navigateToStep(prevStepIndex);
      } else {
        // Page-specific tour - just decrement step
        setCurrentStep(prevStepIndex);
      }
    }
  };

  const skipTour = () => {
    endTour();
  };

  const hideTourPermanently = () => {
    localStorage.setItem('flowdocs_tour_hidden_permanently', 'true');
    setHasHiddenTourPermanently(true);
    endTour();
  };

  const resetTourSettings = () => {
    localStorage.removeItem('flowdocs_tour_hidden_permanently');
    localStorage.removeItem('flowdocs_tour_completed');
    setHasHiddenTourPermanently(false);
    setHasSeenTour(false);
    setIsActive(false);
  };

  return (
    <TourContext.Provider value={{
      isActive,
      currentStep,
      tourSteps,
      hasHiddenTourPermanently,
      startTour,
      startPageTour,
      endTour,
      nextStep,
      prevStep,
      skipTour,
      hideTourPermanently,
      resetTourSettings
    }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
}